// Modal primitive. Centers a white card over a navy backdrop; traps
// Escape to close; outside-click to close; and traps focus inside the
// sheet while open (audit item 3 — shares ui.tsx's useFocusTrap so this
// primitive matches the accessibility of the ui.tsx Modal without a
// disruptive DOM/CSS reroute of its ~29 consumers).
//
// Reuses the .overlay-backdrop rule already in styles.css (shared
// with the keyboard cheat sheet + global search).

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import { useFocusTrap } from "../ui";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Extra className for the modal container (e.g. "modal-narrow"). */
  className?: string;
  children: ReactNode;
  /** Footer slot for action buttons. Renders in .modal-footer. */
  footer?: ReactNode;
  /** Test id applied to the modal container. */
  "data-testid"?: string;
}

export function Modal({
  open,
  onClose,
  title,
  className,
  children,
  footer,
  "data-testid": testId,
}: ModalProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null);

  // Trap focus inside the sheet while open, restore it on close, and move
  // focus in on mount. Shared with the ui.tsx Modal (audit item 3).
  useFocusTrap(open, sheetRef);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    function onMouseDown(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!sheetRef.current || !target) return;
      // Click inside our own sheet — never close.
      if (sheetRef.current.contains(target)) return;
      // Baker bug 2026-05-28: stacked-modal click-eating. When a CHILD
      // Modal opens (e.g. DestructiveActionConfirm rendered inside the
      // MergeReviewWizard) it portals to document.body — so it's a
      // DOM SIBLING of this Modal, not a descendant. Without this
      // guard, every click on the child's buttons satisfies
      // "outside our sheet" and we close ourselves mid-mousedown,
      // unmounting the child before its onClick can fire. Result:
      // Baker clicked "Merge contacts" three times today and the
      // POST never went out. Skip closing whenever the click landed
      // inside ANY [role="dialog"] — the child handles its own
      // outside-click behavior.
      if (target.closest('[role="dialog"]')) return;
      onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open, onClose]);

  if (!open) return null;
  // Render via createPortal to document.body so the modal escapes any
  // parent stacking context (e.g. the sidebar, EmailSidebarSection,
  // dashboard panels) that would otherwise rank dashboard tiles above
  // the modal even though .overlay-backdrop is z-index 1000+. Without
  // the portal, FolderPrefsDialog (opened from inside the EMAIL sidebar
  // section) renders behind the dashboard's pulse + pipeline cards on
  // /dashboard. Same fix as Drawer.tsx — see the comment block there
  // for the full rationale.
  const tree = (
    <div className="overlay-backdrop" role="presentation">
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`modal${className ? ` ${className}` : ""}`}
        data-testid={testId}
      >
        <div className="modal-header">
          <h2>{title}</h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close dialog"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
  if (typeof document === "undefined") return tree;
  return createPortal(tree, document.body);
}
